import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import { api } from "../../services/api";
import "./AdminDashboard.css";

const prettyFieldName = (name = "") =>
  name
    .replace(/_/g, " ")
    .replace(/\bhoa\b/gi, "HOA")
    .replace(/\bcoa\b/gi, "COA")
    .replace(/\brofr\b/gi, "ROFR")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const formatDate = (value) => {
  if (!value) return "—";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
};

const formatSqlValue = (value) => {
  if (value === null || value === undefined) return "NULL";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const DEFAULT_SQL_QUERY = `SELECT id, email, account_status, run_counter, created_at
FROM users
ORDER BY id DESC
LIMIT 25`;

const statusClass = (status) =>
  ["active", "trialing", "enabled"].includes(status)
    ? "good"
    : ["suspended", "unpaid", "canceled"].includes(status)
      ? "bad"
      : "neutral";

const emptyTemplate = () => ({
  name: "",
  default_group: "redp",
  sections: [{ name: "New Section", fields: [] }],
});

function TemplateEditor({ template, availableFields, onCancel, onSaved }) {
  const [draft, setDraft] = useState(template || emptyTemplate());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const fieldById = useMemo(
    () => new Map(availableFields.map((field) => [field.id, field])),
    [availableFields]
  );
  const selectedFieldIds = new Set(
    draft.sections.flatMap((section) => section.fields.map((field) => field.field_id))
  );

  const updateSection = (index, updates) => {
    setDraft((current) => ({
      ...current,
      sections: current.sections.map((section, sectionIndex) =>
        sectionIndex === index ? { ...section, ...updates } : section
      ),
    }));
  };

  const moveSection = (index, offset) => {
    const nextIndex = index + offset;
    if (nextIndex < 0 || nextIndex >= draft.sections.length) return;
    setDraft((current) => {
      const sections = [...current.sections];
      [sections[index], sections[nextIndex]] = [sections[nextIndex], sections[index]];
      return { ...current, sections };
    });
  };

  const addField = (sectionIndex, fieldId) => {
    const id = Number(fieldId);
    if (!id) return;
    const section = draft.sections[sectionIndex];
    if (section.fields.some((field) => field.field_id === id)) return;
    const field = fieldById.get(id);
    updateSection(sectionIndex, {
      fields: [
        ...section.fields,
        {
          field_id: id,
          field_name: field?.field_name || "",
          field_type: field?.field_type || "str",
          display_name: prettyFieldName(field?.field_name),
        },
      ],
    });
  };

  const updateField = (sectionIndex, fieldIndex, updates) => {
    const section = draft.sections[sectionIndex];
    updateSection(sectionIndex, {
      fields: section.fields.map((field, index) =>
        index === fieldIndex ? { ...field, ...updates } : field
      ),
    });
  };

  const moveField = (sectionIndex, fieldIndex, offset) => {
    const section = draft.sections[sectionIndex];
    const nextIndex = fieldIndex + offset;
    if (nextIndex < 0 || nextIndex >= section.fields.length) return;
    const fields = [...section.fields];
    [fields[fieldIndex], fields[nextIndex]] = [fields[nextIndex], fields[fieldIndex]];
    updateSection(sectionIndex, { fields });
  };

  const save = async () => {
    setError("");
    if (!draft.name.trim()) return setError("Template name is required.");
    if (!draft.sections.length) return setError("Add at least one section.");
    if (draft.sections.some((section) => !section.name.trim())) {
      return setError("Every section needs a name.");
    }
    if (draft.sections.some((section) => !section.fields.length)) {
      return setError("Every section needs at least one field.");
    }

    const payload = {
      name: draft.name.trim(),
      default_group: draft.default_group.trim() || "redp",
      sections: draft.sections.map((section) => ({
        name: section.name.trim(),
        fields: section.fields.map((field) => ({
          field_id: field.field_id,
          display_name: field.display_name.trim() || prettyFieldName(field.field_name),
        })),
      })),
    };

    setSaving(true);
    try {
      if (draft.id) await api.put(`/admin/default-customizations/${draft.id}`, payload);
      else await api.post("/admin/default-customizations", payload);
      onSaved();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="admin-panel template-editor">
      <div className="admin-panel-heading">
        <div>
          <p className="admin-eyebrow">System template</p>
          <h2>{draft.id ? "Edit default customization" : "Create default customization"}</h2>
          <p>Saving is atomic. Users on the live fallback see the update after the save completes.</p>
        </div>
        <div className="admin-actions">
          <button className="admin-button secondary" onClick={onCancel}>Cancel</button>
          <button className="admin-button primary" onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save template"}
          </button>
        </div>
      </div>

      {error && <div className="admin-alert">{error}</div>}

      <div className="template-meta-grid">
        <label>
          Template name
          <input value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} />
        </label>
        <label>
          Product group
          <input value={draft.default_group} onChange={(event) => setDraft({ ...draft, default_group: event.target.value })} />
        </label>
      </div>

      <div className="template-sections">
        {draft.sections.map((section, sectionIndex) => {
          return (
            <article className="template-section-card" key={section.id || `section-${sectionIndex}`}>
              <div className="template-section-heading">
                <input
                  aria-label={`Section ${sectionIndex + 1} name`}
                  value={section.name}
                  onChange={(event) => updateSection(sectionIndex, { name: event.target.value })}
                />
                <div className="admin-actions compact">
                  <button onClick={() => moveSection(sectionIndex, -1)} disabled={sectionIndex === 0}>↑</button>
                  <button onClick={() => moveSection(sectionIndex, 1)} disabled={sectionIndex === draft.sections.length - 1}>↓</button>
                  <button
                    className="danger-link"
                    onClick={() => setDraft((current) => ({
                      ...current,
                      sections: current.sections.filter((_, index) => index !== sectionIndex),
                    }))}
                    disabled={draft.sections.length === 1}
                  >
                    Remove
                  </button>
                </div>
              </div>

              <div className="template-field-list">
                {section.fields.map((field, fieldIndex) => (
                  <div className="template-field-row" key={`${sectionIndex}-${field.field_id}`}>
                    <div>
                      <strong>{field.field_name || fieldById.get(field.field_id)?.field_name}</strong>
                      <span>{field.field_type || fieldById.get(field.field_id)?.field_type}</span>
                    </div>
                    <input
                      aria-label={`Display name for ${field.field_name}`}
                      value={field.display_name}
                      onChange={(event) => updateField(sectionIndex, fieldIndex, { display_name: event.target.value })}
                    />
                    <div className="admin-actions compact">
                      <button onClick={() => moveField(sectionIndex, fieldIndex, -1)} disabled={fieldIndex === 0}>↑</button>
                      <button onClick={() => moveField(sectionIndex, fieldIndex, 1)} disabled={fieldIndex === section.fields.length - 1}>↓</button>
                      <button
                        className="danger-link"
                        onClick={() => updateSection(sectionIndex, {
                          fields: section.fields.filter((_, index) => index !== fieldIndex),
                        })}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
                {!section.fields.length && <p className="admin-empty">No fields in this section.</p>}
              </div>

              <select value="" onChange={(event) => addField(sectionIndex, event.target.value)}>
                <option value="">Add a system field…</option>
                {availableFields.filter((field) => !selectedFieldIds.has(field.id)).map((field) => (
                  <option key={field.id} value={field.id}>{prettyFieldName(field.field_name)}</option>
                ))}
              </select>
            </article>
          );
        })}
      </div>

      <button
        className="admin-button secondary"
        onClick={() => setDraft((current) => ({
          ...current,
          sections: [...current.sections, { name: "New Section", fields: [] }],
        }))}
      >
        Add section
      </button>
    </section>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useContext(AuthContext);
  const [tab, setTab] = useState("users");
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [availableFields, setAvailableFields] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [sqlQuery, setSqlQuery] = useState(DEFAULT_SQL_QUERY);
  const [sqlResult, setSqlResult] = useState(null);
  const [sqlRunning, setSqlRunning] = useState(false);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [overviewData, userData, templateData, fieldData] = await Promise.all([
        api.get("/admin/overview"),
        api.get("/admin/users?limit=250"),
        api.get("/admin/default-customizations"),
        api.get("/admin/default-fields"),
      ]);
      setOverview(overviewData);
      setUsers(userData.users || []);
      setTemplates(templateData || []);
      setAvailableFields(fieldData || []);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  const filteredUsers = users.filter((item) => {
    const needle = search.toLowerCase().trim();
    return !needle || `${item.first_name} ${item.last_name} ${item.email}`.toLowerCase().includes(needle);
  });

  const changeAccountStatus = async (targetUser, accountStatus) => {
    try {
      await api.put(`/admin/users/${targetUser.id}/account-status`, { account_status: accountStatus });
      setUsers((current) => current.map((item) =>
        item.id === targetUser.id ? { ...item, account_status: accountStatus } : item
      ));
      setOverview((current) => current ? {
        ...current,
        enabled_users: current.enabled_users + (accountStatus === "enabled" ? 1 : -1),
        suspended_users: current.suspended_users + (accountStatus === "suspended" ? 1 : -1),
      } : current);
    } catch (requestError) {
      setError(requestError.message);
    }
  };

  const editTemplate = async (templateId) => {
    setLoading(true);
    try {
      setEditingTemplate(await api.get(`/admin/default-customizations/${templateId}`));
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  };

  const publishTemplate = async (templateId) => {
    if (!window.confirm("Make this the live default for REDP users without a personal customization?")) return;
    try {
      await api.post(`/admin/default-customizations/${templateId}/publish`, {});
      await loadDashboard();
    } catch (requestError) {
      setError(requestError.message);
    }
  };

  const deleteTemplate = async (templateId) => {
    if (!window.confirm("Delete this inactive system template?")) return;
    try {
      await api.delete(`/admin/default-customizations/${templateId}`);
      await loadDashboard();
    } catch (requestError) {
      setError(requestError.message);
    }
  };

  const finishEditing = async () => {
    setEditingTemplate(null);
    await loadDashboard();
  };

  const runSqlQuery = async () => {
    setError("");
    setSqlRunning(true);
    try {
      setSqlResult(await api.post("/admin/sql-console", {
        query: sqlQuery,
        max_rows: 100,
      }));
    } catch (requestError) {
      setSqlResult(null);
      setError(requestError.message);
    } finally {
      setSqlRunning(false);
    }
  };

  return (
    <div className="admin-page">
      <header className="admin-topbar">
        <div>
          <p className="admin-eyebrow">WaterviewAI operations</p>
          <h1>System administration</h1>
        </div>
        <div className="admin-actions">
          <Link className="admin-button secondary" to={`/redp/${user.id}`}>Back to REDP</Link>
          <button className="admin-button ghost" onClick={logout}>Log out</button>
        </div>
      </header>

      {error && <div className="admin-alert">{error}<button onClick={() => setError("")}>×</button></div>}

      {overview && (
        <section className="admin-metrics">
          <article><span>Total users</span><strong>{overview.total_users}</strong></article>
          <article><span>Enabled</span><strong>{overview.enabled_users}</strong></article>
          <article><span>Suspended</span><strong>{overview.suspended_users}</strong></article>
          <article><span>Paid / trialing</span><strong>{overview.active_subscriptions}</strong></article>
        </section>
      )}

      <nav className="admin-tabs" aria-label="Administration sections">
        <button className={tab === "users" ? "active" : ""} onClick={() => { setTab("users"); setEditingTemplate(null); }}>Users & billing</button>
        <button className={tab === "templates" ? "active" : ""} onClick={() => setTab("templates")}>Default customizations</button>
        <button className={tab === "sql" ? "active" : ""} onClick={() => { setTab("sql"); setEditingTemplate(null); }}>SQL console</button>
      </nav>

      {loading && <div className="admin-loading">Loading administration data…</div>}

      {!loading && tab === "users" && (
        <section className="admin-panel">
          <div className="admin-panel-heading">
            <div>
              <h2>User access and subscription status</h2>
              <p>Billing status is provider-synchronized and read-only here. Account status controls operational access.</p>
            </div>
            <input className="admin-search" type="search" placeholder="Search users" value={search} onChange={(event) => setSearch(event.target.value)} />
          </div>
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead><tr><th>User</th><th>Account</th><th>Subscription</th><th>Plan / renewal</th><th>Runs</th><th>Joined</th></tr></thead>
              <tbody>
                {filteredUsers.map((item) => (
                  <tr key={item.id}>
                    <td><strong>{item.first_name} {item.last_name}</strong><span>{item.email}</span>{item.user_type === "admin" && <em>Admin</em>}</td>
                    <td>
                      <select
                        value={item.account_status}
                        disabled={item.id === user.id}
                        onChange={(event) => changeAccountStatus(item, event.target.value)}
                        className={`status-select ${statusClass(item.account_status)}`}
                      >
                        <option value="enabled">Enabled</option>
                        <option value="suspended">Suspended</option>
                      </select>
                    </td>
                    <td><span className={`status-pill ${statusClass(item.subscription.status)}`}>{item.subscription.status.replace(/_/g, " ")}</span></td>
                    <td><strong>{item.subscription.plan_code || "—"}</strong><span>{formatDate(item.subscription.current_period_end)}</span></td>
                    <td>{item.run_counter}</td>
                    <td>{formatDate(item.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {!loading && tab === "templates" && editingTemplate && (
        <TemplateEditor
          template={editingTemplate.id ? editingTemplate : null}
          availableFields={availableFields}
          onCancel={() => setEditingTemplate(null)}
          onSaved={finishEditing}
        />
      )}

      {!loading && tab === "templates" && !editingTemplate && (
        <section className="admin-panel">
          <div className="admin-panel-heading">
            <div>
              <h2>System default customizations</h2>
              <p>The live template is used whenever a REDP user has no active personal customization.</p>
            </div>
            <button className="admin-button primary" onClick={() => setEditingTemplate(emptyTemplate())}>New template</button>
          </div>
          <div className="template-grid">
            {templates.map((template) => (
              <article className={`template-card ${template.active ? "live" : ""}`} key={template.id}>
                <div>
                  <span className={`status-pill ${template.active ? "good" : "neutral"}`}>{template.active ? "Live default" : "Available"}</span>
                  <h3>{template.name}</h3>
                  <p>{template.section_count} sections · Group: {template.default_group}</p>
                  {template.active && <p>{template.fallback_user_count} users currently inherit this default</p>}
                </div>
                <div className="admin-actions">
                  <button className="admin-button secondary" onClick={() => editTemplate(template.id)}>Edit</button>
                  {!template.active && <button className="admin-button primary" onClick={() => publishTemplate(template.id)}>Make live</button>}
                  {!template.active && <button className="admin-button danger" onClick={() => deleteTemplate(template.id)}>Delete</button>}
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {!loading && tab === "sql" && (
        <section className="admin-panel sql-console">
          <div className="admin-panel-heading">
            <div>
              <h2>Read-only SQL console</h2>
              <p>
                Queries use the database configured by SQL_CONNECTION_STRING. One
                SELECT, WITH, or EXPLAIN statement is allowed, with a five-second
                timeout and a 200-row hard limit.
              </p>
            </div>
            <button
              className="admin-button primary"
              onClick={runSqlQuery}
              disabled={sqlRunning || !sqlQuery.trim()}
            >
              {sqlRunning ? "Running…" : "Run query"}
            </button>
          </div>

          <label className="sql-editor-label" htmlFor="admin-sql-query">SQL query</label>
          <textarea
            id="admin-sql-query"
            className="sql-editor"
            value={sqlQuery}
            onChange={(event) => setSqlQuery(event.target.value)}
            spellCheck="false"
          />
          <p className="sql-safety-note">
            Write operations, multiple statements, comments, and credential or token
            fields are blocked. Query executions are recorded in the admin audit log.
          </p>

          {sqlResult && (
            <div className="sql-results">
              <div className="sql-result-meta">
                <strong>{sqlResult.row_count} row{sqlResult.row_count === 1 ? "" : "s"}</strong>
                <span>{sqlResult.database} · {sqlResult.duration_ms} ms</span>
                {sqlResult.truncated && <span>Results truncated at {sqlResult.max_rows} rows</span>}
              </div>
              <div className="admin-table-wrap">
                <table className="admin-table sql-result-table">
                  <thead>
                    <tr>{sqlResult.columns.map((column) => <th key={column}>{column}</th>)}</tr>
                  </thead>
                  <tbody>
                    {sqlResult.rows.map((row, rowIndex) => (
                      <tr key={rowIndex}>
                        {sqlResult.columns.map((column) => (
                          <td key={`${rowIndex}-${column}`} title={formatSqlValue(row[column])}>
                            {formatSqlValue(row[column])}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {!sqlResult.rows.length && <p className="admin-empty">The query returned no rows.</p>}
            </div>
          )}
        </section>
      )}
    </div>
  );
}
